import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import './App.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { useInactivityLogout } from './hooks/useInactivityLogout';

// ── Eagerly loaded (always needed) ───────────────────────────
import LoginPage      from './components/auth//Login/LoginPage';
import InternalLayout from './components/layout/InternalLayout';
import ExternalLayout from './components/layout/ExternalLayout';
import Dashboard      from './pages/internal/Dashboard/Dashboard';
import SurveysList    from './pages/internal/SurveyList/SurveyList';
import PastSurveys    from './pages/internal/PastSurveys/PastSurveys';
import Profile        from './shared/Profile/Profile';
import DraftSurveys   from './pages/internal/DraftSurveys/DraftSurveys';
import CreateSurvey   from './pages/internal/CreateSurvey/CreateSurvey';
import StaffLoginPage    from './components/auth/Login/StaffLoginPage';
import ProviderLoginPage from './components/auth/Login/ProviderLoginPage';

// ── Lazy loaded ───────────────────────────────────────────────
const ForgotPasswordPage = React.lazy(() => import('./components/auth/Login/ForgotPasswordPage'));
const ExportData         = React.lazy(() => import('./pages/internal/ExportData/ExportData'));
const ResetPasswordPage  = React.lazy(() => import('./components/auth/Login/ResetPasswordPage'));
const SurveyDetail      = React.lazy(() => import('./pages/internal/SurveyDetail/SurveyDetail'));
const Establishments    = React.lazy(() => import('./pages/internal/Establishments/Establishment'));
const DataProviders     = React.lazy(() => import('./pages/internal/DataProviders/DataProvider'));
const AdminDashboard    = React.lazy(() => import('./pages/admin/AdminDashboard/AdminDashboard'));
const ExpenditureItems  = React.lazy(() => import('./pages/admin/ExpenditureItems/ExpenditureItems'));
const UnitsOfMeasurement = React.lazy(() => import('./pages/admin/UnitsOfMeasurement/UnitsOfMeasurement'));
const AuditLogs         = React.lazy(() => import('./pages/admin/AuditLogs/AuditLogs'));
const ExternalDashboard = React.lazy(() => import('./pages/external/ExternalDashboard/ExternalDashboard'));
const ExternalPastSurveys = React.lazy(() => import('./pages/external/PastSurveys/PastSurveys'));
const SurveyForm        = React.lazy(() => import('./pages/external/SurveyForm/SurveyForm'));
const ProviderSettings  = React.lazy(() => import('./pages/external/ProviderSettings/ProviderSettings'));



// ── Route guards ──────────────────────────────────────────────
const RequireAuth = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading"><span>Loading…</span></div>;
  // Routes scoped to data providers only (e.g. /external/*) should bounce an
  // unauthenticated/wrong-role visitor to the provider login, not the staff
  // one — otherwise logging out from the provider side (which clears `user`
  // and re-triggers this guard) races the explicit post-logout navigate and
  // can land on the staff login page instead.
  const loginPath = (roles?.length === 1 && roles[0] === 'data_provider') ? '/provider-login' : '/login';
  if (!user)   return <Navigate to={loginPath} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={loginPath} replace />;
  return children;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading"><span>Loading…</span></div>;
  if (!user)                             return <Navigate to="/login"             replace />;
  if (user.role === 'admin')             return <Navigate to="/admin/dashboard"   replace />;
  if (user.role === 'internal')          return <Navigate to="/internal/dashboard" replace />;
  if (user.role === 'data_provider')     return <Navigate to="/external/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

const PageLoader = () => (
  <div className="app-loading"><span>Loading page…</span></div>
);

// Renders nothing — just runs the 30-minute idle-logout watcher for
// whichever user (internal/admin/data provider) is currently signed in.
const InactivityWatcher = () => {
  useInactivityLogout();
  return null;
};

// ── App ───────────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>
      <InactivityWatcher />
      <BrowserRouter>
        <React.Suspense fallback={<PageLoader />}>
          <Routes>

            {/* ── Public ───────────────────────────────────── */}
            <Route path="/"       element={<RootRedirect />} />
            {/* <Route path="/login"           element={<LoginPage />} /> */}
            <Route path="/login"                element={<StaffLoginPage />} />
            <Route path="/provider-login"       element={<ProviderLoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password"  element={<ResetPasswordPage />} />

            {/* ── Internal Staff (internal + admin share layout) */}
            <Route
              path="/internal"
              element={
                <RequireAuth roles={['internal', 'admin']}>
                  <InternalLayout />
                </RequireAuth>
              }
            >
              <Route index                    element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"         element={<Dashboard />} />
              <Route path="surveys"           element={<SurveysList />} />
              <Route path="surveys/past"      element={<PastSurveys />} />
              <Route path="profile"           element={<Profile />} />
              <Route path="surveys/drafts"    element={<DraftSurveys />} />
              <Route path="surveys/create"    element={<CreateSurvey />} />
              <Route path="surveys/:id"       element={<SurveyDetail />} />
              <Route path="surveys/:id/edit-response" element={<SurveyForm />} />
              <Route path="surveys/:id/fill/:dpId" element={<SurveyForm />} />
              <Route path="establishments"    element={<Establishments />} />
              <Route path="providers"         element={<DataProviders />} />
              <Route path="export"            element={<ExportData />} />
            </Route>

            {/* ── Admin (also uses InternalLayout + all /internal routes) */}
            <Route
              path="/admin"
              element={
                <RequireAuth roles={['admin']}>
                  <InternalLayout />
                </RequireAuth>
              }
            >
              <Route index             element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"  element={<AdminDashboard />} />
              {/* Admin can also access internal routes */}
              <Route path="surveys"           element={<SurveysList />} />
              <Route path="surveys/past"      element={<PastSurveys />} />
              <Route path="profile"           element={<Profile />} />
              <Route path="surveys/drafts"    element={<DraftSurveys />} />
              <Route path="surveys/create"    element={<CreateSurvey />} />
              <Route path="surveys/:id"       element={<SurveyDetail />} />
              <Route path="surveys/:id/edit-response" element={<SurveyForm />} />
              <Route path="surveys/:id/fill/:dpId" element={<SurveyForm />} />
              <Route path="establishments"    element={<Establishments />} />
              <Route path="providers"         element={<DataProviders />} />
              <Route path="export"            element={<ExportData />} />
              <Route path="expenditure-items" element={<ExpenditureItems />} />
              <Route path="units-of-measurement" element={<UnitsOfMeasurement />} />
              <Route path="audit-logs"        element={<AuditLogs />} />
            </Route>

            {/* ── External / Data Providers ─────────────────── */}
            <Route
              path="/external"
              element={
                <RequireAuth roles={['data_provider']}>
                  <ExternalLayout />
                </RequireAuth>
              }
            >
              <Route index                   element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"        element={<ExternalDashboard />} />
              <Route path="surveys/past"     element={<ExternalPastSurveys />} />
              <Route path="survey/:id"       element={<SurveyForm />} />
              <Route path="settings"         element={<ProviderSettings />} />
            </Route>

            {/* ── Misc ─────────────────────────────────────── */}

            <Route path="/unauthorized" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </React.Suspense>
      </BrowserRouter>

      <ToastContainer
        position="top-right"
        autoClose={4500}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme="colored"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.875rem' }}
      />
    </AuthProvider>
  );
}

export default App;